import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../auth/AuthContext";
import { supabase } from "../lib/supabase";
import { buildBunnyPlayerUrl } from "../lib/video";
import type { Dorama } from "../types";

type Media = {
  dorama_id: string;
  full_video_id: string | null;
  full_video_id_sub: string | null;
  full_video_id_dub: string | null;
};

type Episode = {
  id: string;
  title: string;
  episode_order: number;
  start_time: number;
  is_preview: boolean;
};

export function ProtectedPlayer({ dorama }: { dorama: Dorama }) {
  const { user, loading: authLoading } = useAuth();
  const [media, setMedia] = useState<Media | null>(null);
  const [episodes, setEpisodes] = useState<Episode[]>([]);
  const [loading, setLoading] = useState(true);
  const [language, setLanguage] = useState<"dub" | "sub">("dub");
  const [startTime, setStartTime] = useState(0);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setMedia(null);
    setStartTime(0);

    const episodesRequest = supabase.from("episodes")
      .select("id, title, episode_order, start_time, is_preview")
      .eq("dorama_id", dorama.id)
      .order("episode_order", { ascending: true });
    const mediaRequest = supabase.from("dorama_media")
      .select("dorama_id, full_video_id, full_video_id_sub, full_video_id_dub")
      .eq("dorama_id", dorama.id)
      .maybeSingle();

    Promise.all([episodesRequest, mediaRequest]).then(([episodeResult, mediaResult]) => {
      if (!active) return;
      setEpisodes((episodeResult.data ?? []) as Episode[]);
      setMedia((mediaResult.data ?? null) as Media | null);
      const nextMedia = mediaResult.data as Media | null;
      if (!nextMedia?.full_video_id_dub) setLanguage("sub");
      setLoading(false);
    });

    return () => { active = false; };
  }, [dorama.id, user]);

  const hasAccess = Boolean(media);
  const dubId = media?.full_video_id_dub ?? null;
  const subId = media?.full_video_id_sub || media?.full_video_id || null;
  const fullId = language === "dub" && dubId ? dubId : subId || dubId;
  const videoId = hasAccess ? fullId : dorama.trailer_video_id || dorama.trailer_url;
  const playerUrl = useMemo(() => {
    return buildBunnyPlayerUrl(videoId, { startTime: hasAccess ? startTime : 0 });
  }, [hasAccess, startTime, videoId]);

  return (
    <section className="watch-section" aria-labelledby="watch-title">
      <div className="watch-heading">
        <div><p className="eyebrow">{hasAccess ? "CONTEÚDO LIBERADO" : "ASSISTA AO TRAILER"}</p><h2 id="watch-title">{hasAccess ? dorama.title : "Conheça esta novelinha"}</h2></div>
        {hasAccess && dubId && subId && <div className="language-switch" aria-label="Idioma do vídeo"><button className={language === "dub" ? "active" : ""} onClick={() => { setLanguage("dub"); setStartTime(0); }}>Dublado</button><button className={language === "sub" ? "active" : ""} onClick={() => { setLanguage("sub"); setStartTime(0); }}>Legendado</button></div>}
      </div>

      <div className="video-frame">
        {(loading || authLoading) && <div className="video-placeholder">Verificando sua liberação…</div>}
        {!loading && !authLoading && playerUrl && <iframe key={`${videoId}-${startTime}`} src={playerUrl} title={`${dorama.title} — ${hasAccess ? "vídeo completo" : "trailer"}`} loading="eager" allow="accelerometer; gyroscope; autoplay; encrypted-media; picture-in-picture; fullscreen" allowFullScreen referrerPolicy="strict-origin-when-cross-origin" />}
        {!loading && !authLoading && !playerUrl && <div className="video-placeholder">Vídeo indisponível no momento.</div>}
      </div>

      {hasAccess && episodes.length > 0 && <div className="episode-picker"><h3>Episódios</h3><div>{episodes.map((episode) => <button key={episode.id} className={startTime === episode.start_time ? "active" : ""} onClick={() => setStartTime(episode.start_time)}><span>{episode.episode_order}</span>{episode.title}</button>)}</div></div>}

      {!hasAccess && !authLoading && <div className="locked-message">
        {user ? <><strong>Conteúdo completo bloqueado</strong><p>Esta conta ainda não possui acesso a esta novelinha. A compra online será conectada na próxima etapa; o administrador já pode liberar o título manualmente.</p><Link className="secondary-button" to="/vip">Conhecer o Passe Vitalício</Link></> : <><strong>Entre para verificar seu acesso</strong><p>Compras e o Passe Vitalício ficam vinculados à sua conta.</p><Link className="primary-button" to="?entrar=1">Entrar na minha conta</Link></>}
      </div>}
    </section>
  );
}
